<template>
	<v-dialog
		v-model="internalOpen"
		max-width="460px"
		persistent
	>
		<v-card dark color="#1E1E1E">

			<!-- Title bar -->
			<v-card-title class="pa-3" style="background: #2D2D2D;">
				<v-icon small class="mr-2" color="orange lighten-1">mdi-cog-outline</v-icon>
				<span class="subtitle-2 font-weight-bold">{{ $ml.get('alarm_stats_shift_config') }}</span>
				<v-spacer></v-spacer>
				<v-btn icon x-small @click="internalOpen = false">
					<v-icon small>mdi-close</v-icon>
				</v-btn>
			</v-card-title>

			<v-divider></v-divider>

			<!-- Form -->
			<v-card-text class="pt-4 pb-2">

				<!-- Shift Start -->
				<v-text-field
					v-model="form.shift_start"
					dense
					outlined
					dark
					:label="$ml.get('alarm_stats_shift_start')"
					type="time"
					:hint="$ml.get('alarm_stats_shift_start_hint')"
					persistent-hint
					prepend-inner-icon="mdi-clock-start"
					class="mb-2"
					:error-messages="errors.shift_start"
					@input="clearError('shift_start')"
				></v-text-field>

				<!-- Shift Duration -->
				<v-text-field
					v-model.number="form.shift_duration_min"
					dense
					outlined
					dark
					:label="$ml.get('alarm_stats_shift_dur_min')"
					type="number"
					suffix="min"
					:hint="$ml.get('alarm_stats_shift_duration_hint')"
					persistent-hint
					prepend-inner-icon="mdi-timer-outline"
					class="mb-2"
					:error-messages="errors.shift_duration_min"
					@input="clearError('shift_duration_min')"
				></v-text-field>

				<!-- Target Rate -->
				<v-text-field
					v-model.number="form.target_rate"
					dense
					outlined
					dark
					:label="$ml.get('alarm_stats_target_rate')"
					type="number"
					suffix="u/min"
					:hint="$ml.get('alarm_stats_target_rate_hint')"
					persistent-hint
					prepend-inner-icon="mdi-speedometer"
					class="mb-2"
					:error-messages="errors.target_rate"
					@input="clearError('target_rate')"
				></v-text-field>

				<!-- Stoppage Threshold -->
				<v-text-field
					v-model.number="form.stoppage_threshold_ms"
					dense
					outlined
					dark
					:label="$ml.get('alarm_stats_stop_threshold')"
					type="number"
					suffix="ms"
					:hint="$ml.get('alarm_stats_stop_threshold_hint')"
					persistent-hint
					prepend-inner-icon="mdi-alert-octagon-outline"
					class="mb-2"
					:error-messages="errors.stoppage_threshold_ms"
					@input="clearError('stoppage_threshold_ms')"
				></v-text-field>

			</v-card-text>

			<v-divider></v-divider>

			<!-- Actions -->
			<v-card-actions class="pa-3">
				<v-spacer></v-spacer>
				<v-btn
					small
					text
				color="grey"
				@click="internalOpen = false"
			>
				{{ $ml.get('alarm_stats_cancel') }}
			</v-btn>
				<v-btn
					small
					color="orange darken-1"
					dark
					:loading="saving"
					@click="handleSave"
				>
				<v-icon small left>mdi-content-save-outline</v-icon>
				{{ $ml.get('save') }}
			</v-btn>
			</v-card-actions>

		</v-card>
	</v-dialog>
</template>

<script>
export default {
	name: 'StatsShiftConfig',

	props: {
		value: {
			// v-model: controls dialog open state
			type    : Boolean,
			default : false,
		},
		config: {
			// current shift config to pre-populate form
			type    : Object,
			default : () => ({
				shift_start          : '08:00',
				shift_duration_min   : 540,
				target_rate          : 0,
				stoppage_threshold_ms: 5000,
			}),
		},
	},

	data() {
		return {
			saving : false,
			form   : {
				shift_start          : '08:00',
				shift_duration_min   : 540,
				target_rate          : 0,
				stoppage_threshold_ms: 5000,
			},
			errors: {
				shift_start          : '',
				shift_duration_min   : '',
				target_rate          : '',
				stoppage_threshold_ms: '',
			},
		};
	},

	computed: {
		internalOpen: {
			get() {
				return this.value;
			},
			set(val) {
				this.$emit('input', val);
			},
		},
	},

	watch: {
		// When dialog opens, pre-populate form from current config
		value(newVal) {
			if (newVal) {
				this.populateForm();
				this.clearAllErrors();
			}
		},
	},

	methods: {

		populateForm() {
			const cfg = this.config || {};
			this.form.shift_start           = cfg.shift_start           || '08:00';
			this.form.shift_duration_min    = cfg.shift_duration_min    != null ? cfg.shift_duration_min    : 540;
			this.form.target_rate           = cfg.target_rate           != null ? cfg.target_rate           : 0;
			this.form.stoppage_threshold_ms = cfg.stoppage_threshold_ms != null ? cfg.stoppage_threshold_ms : 5000;
		},

		clearError(field) {
			this.errors[field] = '';
		},

		clearAllErrors() {
			Object.keys(this.errors).forEach(k => { this.errors[k] = ''; });
		},

		validate() {
			let valid = true;

			// shift_start: required, HH:MM format
			if (!this.form.shift_start || !/^\d{2}:\d{2}$/.test(this.form.shift_start)) {
				this.errors.shift_start = this.$ml.get('alarm_stats_shift_start_invalid');
				valid = false;
			}

			// shift_duration_min: must be > 0
			const dur = Number(this.form.shift_duration_min);
			if (isNaN(dur) || dur <= 0) {
				this.errors.shift_duration_min = this.$ml.get('alarm_stats_shift_duration_invalid');
				valid = false;
			}

			// target_rate: must be >= 0
			const rate = Number(this.form.target_rate);
			if (isNaN(rate) || rate < 0) {
				this.errors.target_rate = this.$ml.get('alarm_stats_target_rate_invalid');
				valid = false;
			}

			// stoppage_threshold_ms: must be >= 1000
			const threshold = Number(this.form.stoppage_threshold_ms);
			if (isNaN(threshold) || threshold < 1000) {
				this.errors.stoppage_threshold_ms = this.$ml.get('alarm_stats_threshold_invalid');
				valid = false;
			}

			return valid;
		},

		handleSave() {
			this.clearAllErrors();
			if (!this.validate()) return;

			const config = {
				shift_start          : this.form.shift_start,
				shift_duration_min   : Number(this.form.shift_duration_min),
				target_rate          : Number(this.form.target_rate),
				stoppage_threshold_ms: Number(this.form.stoppage_threshold_ms),
			};

			this.$emit('save', config);
			this.internalOpen = false;
		},

	}, // methods
};
</script>
